import { createSlice } from '@reduxjs/toolkit';
import { endOfDay, startOfDay, subDays } from 'date-fns';

import { ChartState } from 'types/charts';
import { DateTypes } from 'types/client';
import {
  getActualStatsByProductsChartData,
  getCardsChartData,
  getClientsChartData,
  getCommentsChartData,
  getPamentsByTariffChartData,
  getPaymentChartData,
  getSalesFunnelChartData,
  getStatsByProductsChartData,
  getSubscriptionBaseChartData,
  getSubscriptionsChartData,
  getTrialsChartData,
} from './thunk';

const today = endOfDay(new Date()).toISOString();
const weekAgo = startOfDay(subDays(new Date(), 6)).toISOString();

const initialState: ChartState = {
  orders: {
    data: null,
    change: null,
    description: null,
    loading: false,
    error: null,
  },
  trials: {
    data: null,
    change: null,
    description: null,
    loading: false,
    error: null,
  },
  cards: {
    data: null,
    amount: null,
    change: null,
    loading: false,
    description: null,
  },
  clients: {
    data: null,
    amount: null,
    change: null,
    loading: false,
    description: null,
  },
  subscriptions: {
    data: null,
    amount: null,
    change: null,
    description: null,
    total_amount: null,
    loading: false,
  },
  salesFunnel: {
    data: null,
    amount: null,
    change: null,
    loading: false,
    description: null,
  },
  statsByProduct: {
    stats: null,
    change: null,
    description: null,
    loading: false,
  },
  statsByProductActual: {
    stats: null,
    description: null,
    loading: false,
  },
  comments: {
    amount: null,
    change: null,
    statistic: null,
    description: null,
    loading: false,
  },
  subscriptionBase: {
    series: {},
    description: null,
    loading: false,
  },
  paymentsByTariff: {
    series: {},
    description: null,
    loading: false,
  },
  params: {
    [DateTypes.FROM]: weekAgo,
    [DateTypes.TO]: today,
  },
  chartError: {
    [DateTypes.FROM]: '',
    [DateTypes.TO]: '',
  },
};

const slice = createSlice({
  name: 'charts',
  initialState,
  reducers: {
    setChartsParams: (state, { payload }) => {
      state.params = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentChartData.pending, (state) => {
        state.orders.loading = true;
      })
      .addCase(getPaymentChartData.fulfilled, (state, { payload }) => {
        state.orders.loading = false;
        state.orders = {
          ...state.orders,
          data: payload.data,
          description: payload.description,
          change: payload.change,
        };
      })
      .addCase(getPaymentChartData.rejected, (state) => {
        state.orders.loading = false;
      })

      .addCase(getTrialsChartData.pending, (state) => {
        state.trials.loading = true;
      })
      .addCase(getTrialsChartData.fulfilled, (state, { payload }) => {
        state.trials.loading = false;
        state.trials = {
          ...state.trials,
          data: payload.data,
          description: payload.description,
          change: payload.change,
        };
      })
      .addCase(getTrialsChartData.rejected, (state) => {
        state.trials.loading = false;
      })

      .addCase(getCardsChartData.pending, (state) => {
        state.cards.loading = true;
      })
      .addCase(getCardsChartData.fulfilled, (state, { payload }) => {
        state.cards.loading = false;
        state.cards = { ...state.cards, ...payload };
      })
      .addCase(getCardsChartData.rejected, (state) => {
        state.cards.loading = false;
      })

      .addCase(getClientsChartData.pending, (state) => {
        state.clients.loading = true;
      })
      .addCase(getClientsChartData.fulfilled, (state, { payload }) => {
        state.clients.loading = false;
        state.clients = { ...state.clients, ...payload };
      })
      .addCase(getClientsChartData.rejected, (state) => {
        state.clients.loading = false;
      })

      .addCase(getSubscriptionsChartData.pending, (state) => {
        state.subscriptions.loading = true;
      })
      .addCase(getSubscriptionsChartData.fulfilled, (state, { payload }) => {
        state.subscriptions.loading = false;
        state.subscriptions = { ...state.subscriptions, ...payload };
      })
      .addCase(getSubscriptionsChartData.rejected, (state) => {
        state.subscriptions.loading = false;
      })
      .addCase(getSalesFunnelChartData.pending, (state) => {
        state.salesFunnel.loading = true;
      })
      .addCase(getSalesFunnelChartData.fulfilled, (state, { payload }) => {
        state.salesFunnel.loading = false;

        state.salesFunnel = {
          ...state.salesFunnel,
          amount: payload.data,
          change: payload.change,
        };
      })
      .addCase(getStatsByProductsChartData.pending, (state) => {
        state.statsByProduct.loading = true;
      })
      .addCase(getStatsByProductsChartData.fulfilled, (state, { payload }) => {
        state.statsByProduct.loading = false;
        state.statsByProduct = {
          ...state.statsByProduct,
          stats: payload.data.stats,
          change: payload.data.change,
          description: payload.description,
        };
      })
      .addCase(getStatsByProductsChartData.rejected, (state) => {
        state.statsByProduct.loading = false;
      })
      .addCase(getActualStatsByProductsChartData.pending, (state) => {
        state.statsByProductActual.loading = true;
      })
      .addCase(getActualStatsByProductsChartData.fulfilled, (state, { payload }) => {
        state.statsByProductActual.loading = false;
        state.statsByProductActual = {
          ...state.statsByProductActual,
          stats: payload.data.stats,
          description: payload.description,
        };
      })
      .addCase(getActualStatsByProductsChartData.rejected, (state) => {
        state.statsByProductActual.loading = false;
      })
      .addCase(getCommentsChartData.pending, (state) => {
        state.comments.loading = true;
      })
      .addCase(getCommentsChartData.fulfilled, (state, { payload }) => {
        state.comments = { ...state.comments, ...payload.data, description: payload.description };
        state.comments.loading = false;
      })
      .addCase(getCommentsChartData.rejected, (state) => {
        state.comments.loading = false;
      })
      .addCase(getSubscriptionBaseChartData.pending, (state) => {
        state.subscriptionBase.loading = true;
      })
      .addCase(getSubscriptionBaseChartData.fulfilled, (state, { payload }) => {
        const { series, description } = payload;
        state.subscriptionBase.loading = false;
        state.subscriptionBase.description = description;
        state.subscriptionBase.series = series;
      })
      .addCase(getSubscriptionBaseChartData.rejected, (state) => {
        state.subscriptionBase.loading = false;
      })
      .addCase(getPamentsByTariffChartData.pending, (state) => {
        state.paymentsByTariff.loading = true;
      })
      .addCase(getPamentsByTariffChartData.fulfilled, (state, { payload }) => {
        const { series, description } = payload;
        state.paymentsByTariff.loading = false;
        state.paymentsByTariff.description = description;
        state.paymentsByTariff.series = series;
      })
      .addCase(getPamentsByTariffChartData.rejected, (state) => {
        state.paymentsByTariff.loading = false;
      });
  },
});

export default slice.reducer;

export const { setChartsParams } = slice.actions;
